import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { X, Share2, Copy, Check, RotateCcw, RefreshCw, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function LinkRow({ url }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ }
  };
  return (
    <div className="flex items-center gap-2">
      <code data-testid="share-link-value" className="flex-1 mono text-sm text-[#0B2830] bg-[#F1F6F8] border border-[#D2E0E6] rounded-md px-3 py-2 break-all">{url}</code>
      <button data-testid="share-link-copy" onClick={copy} className="shrink-0 border border-[#D2E0E6] text-[#47616B] p-2 rounded-md hover:bg-[#E0EBEF]">
        {copied ? <Check className="w-4 h-4 text-[#0D9488]" /> : <Copy className="w-4 h-4" />}
      </button>
      <a data-testid="share-link-open" href={url} target="_blank" rel="noreferrer" className="shrink-0 border border-[#D2E0E6] text-[#47616B] p-2 rounded-md hover:bg-[#E0EBEF]">
        <ExternalLink className="w-4 h-4" />
      </a>
    </div>
  );
}

export default function SharePanel({ token, onClose, onRefresh, refreshing }) {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const cfg = { headers: { Authorization: `Bearer ${token}` } };
  const errMsg = (e) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Something went wrong. Please sign in again and retry.");

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/share/settings`, { headers: { Authorization: `Bearer ${token}` } });
      setSettings(res.data); setError(null);
    } catch (e) { setError(errMsg(e)); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn) => {
    setBusy(true);
    try { await fn(); await load(); } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };
  const toggle = () => act(() => axios.post(`${API}/share/toggle`, { enabled: !settings.enabled }, cfg));
  const rotate = () => {
    if (window.confirm("Create a new link? The current link will stop working for anyone who has it.")) act(() => axios.post(`${API}/share/rotate`, {}, cfg));
  };
  const url = settings ? `${window.location.origin}${settings.path}` : "";

  return (
    <div className="fixed inset-0 z-50 bg-[#0B2830]/70 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="share-panel" className="bg-white rounded-xl w-full max-w-lg p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-[#0B2830]"><Share2 className="w-5 h-5 text-[#0284C7]" /><h2 className="text-lg font-bold">Publish report link</h2></div>
          <button data-testid="share-panel-close" onClick={onClose} className="text-[#7C949C] hover:text-[#0B2830]"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-[#47616B] mb-5">
          Share the full screener table with anyone via a private link &mdash; no login or email needed. It updates automatically after each nightly refresh.
        </p>
        {error && <p data-testid="share-error" className="text-sm text-[#E11D48] bg-[#FFF1F2] border border-[#FECDD3] rounded-md px-3 py-2 mb-4">{error}</p>}
        {!settings ? (!error && <p className="text-sm text-[#7C949C]">Loading&hellip;</p>) : (
          <div className="space-y-5">
            <label className="flex items-center justify-between bg-[#F1F6F8] border border-[#D2E0E6] rounded-md px-4 py-3 cursor-pointer">
              <span className="text-sm font-medium text-[#0B2830]">Public link {settings.enabled ? "is live" : "is off"}</span>
              <input data-testid="share-toggle" type="checkbox" checked={settings.enabled} disabled={busy} onChange={toggle} className="w-5 h-5 accent-[#0284C7]" />
            </label>
            {settings.enabled && <LinkRow url={url} />}
            <div className="flex flex-wrap gap-2">
              <button data-testid="share-refresh-data" onClick={onRefresh} disabled={refreshing}
                className="flex items-center gap-1.5 bg-[#0284C7] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#0369A1] disabled:opacity-60">
                <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> {refreshing ? "Refreshing data\u2026" : "Refresh data now"}
              </button>
              <button data-testid="share-rotate" onClick={rotate} disabled={busy}
                className="flex items-center gap-1.5 border border-[#D2E0E6] text-[#47616B] px-4 py-2 rounded-md text-sm hover:bg-[#E0EBEF]">
                <RotateCcw className="w-4 h-4" /> New link
              </button>
            </div>
            <p className="text-xs text-[#7C949C]">"Refresh data now" re-pulls the latest financials (takes ~1&ndash;2 min); viewers can tap Reload on the shared page to see it.</p>
          </div>
        )}
      </div>
    </div>
  );
}
